import WRAP from "WRAP-JS";
import { mps2kt } from "../../../utils/FPEUtils";
import { WX_ID_SYNOP } from "../../../store/WeatherContentsState";
import LegendSynop from "../legend/LegendSynop";
import {
  configSynopDh,
  configSynopVisLayer,
  configSynopWindDirectionLayer,
  configSynopWindSpeedLayer
} from "./config/ConfigSynop";
import TimeController from "../helper/TimeController";
import { nearValidTime } from "../helper/MapUtil";
import MissingDataObserver from "../helper/MissingDataObserver";
import { COMMUNICATE_ERROR_MESSAGE_JMA_SYNOP_1MIN, COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER, COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD } from "../../../constants/ErrorMessage";

const MISSING_THRESHOLD = {
  [WX_ID_SYNOP]: 1800
};

class Synop {

  static NAME = WX_ID_SYNOP;

  dh;
  layerVis;
  layerWindDirection;
  layerWindSpeed;

  ready = false;
  dhAutoUpdate = true;
  visible;
  dispatcher;
  validTime = '---';

  windDirectionVisible;
  windSpeedVisible;

  // タイムコントローラー
  synopTimeController;
  // タイムコントローラーのリスト
  timeList;
  // 自動更新フラグ
  autoRefresh = true;
  errorMessageInfo;

  constructor(config) {
    this.synopTimeController = new TimeController(WX_ID_SYNOP, {
      label: 'SYNOP',
      getTimeList: () => {
        if (this.timeList) {
          return this.timeList;
        }
        this._updateTimeList();
        return this.timeList;
      },
      updated: () => {
        this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
      },
      changeAutoRefresh: (autoFlg) => {
        this.setAutoRefresh(autoFlg);
      }
    });
    this.windDirectionVisible = !!config.windSpdDir;
    this.windSpeedVisible = !!config.windSpdDir;
    this.layerVis = new WRAP.Geo.Layer(`${Synop.NAME}_VIS`);
    this.layerWindDirection = new WRAP.Geo.Layer(`${Synop.NAME}_WIND_DIR`);
    this.layerWindSpeed = new WRAP.Geo.Layer(`${Synop.NAME}_WIND_SPEED`);
    this.layerVis.setVisible(false);
    this.layerWindDirection.setVisible(false);
    this.layerWindSpeed.setVisible(false);
    this.layerWindDirection.disableInteraction(true);
    this.layerWindSpeed.disableInteraction(true);
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
    this.missingDataObserver = new MissingDataObserver(this.dispatcher, MISSING_THRESHOLD, this);
  }

  async initialize() {
    await this.initData();
    this.initLayer();
    this.initTimeController();
    this.ready = true;
  }

  initData() {
    return new Promise(resolve => {
      this.dh = new WRAP.Data(configSynopDh);

      this.dh.validate((displayTime, data, content) => {
        return this._validateTime(data, displayTime, content);
      });

      this.missingDataObserver.monitoring(WX_ID_SYNOP, this.dh, this.layerVis);

      this.dh.updated('timelist', ref => {
        if (this.errorMessageInfo) {
          this.dispatcher({type: 'CLEAR_ERROR', payload: this.errorMessageInfo});
          this.errorMessageInfo = null;
        }
        this._selectValidTime(ref, validTime => {
          this.dh.load('time', {validtime: validTime}, true, null, () => {
            this.dispatcher({type: 'SHOW_ERROR', payload: {
              message: COMMUNICATE_ERROR_MESSAGE_JMA_SYNOP_1MIN,
              replacement: {data: 'time'},
              additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
            }});
          });
        });
      }, () => {
        this.errorMessageInfo = this.dispatcher({type: 'SHOW_ERROR', payload: {
          message: COMMUNICATE_ERROR_MESSAGE_JMA_SYNOP_1MIN,
          replacement: {data: 'timelist'},
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
        }});
      });

      this.dh.updated('master', null, () => {
        this.dispatcher({type: 'SHOW_ERROR', payload: {
          message: COMMUNICATE_ERROR_MESSAGE_JMA_SYNOP_1MIN,
          replacement: {data: 'master'},
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
        }});
      });

      this.dh.updated('info', null, () => {
        this.dispatcher({type: 'SHOW_ERROR', payload: {
          message: COMMUNICATE_ERROR_MESSAGE_JMA_SYNOP_1MIN,
          replacement: {data: 'info'},
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
        }});
      });

      this.dh.updated('data', ref => {
        resolve(ref);
      });
    });
  }

  initLayer() {
    this.layerVis.configure(this.dh, configSynopVisLayer);
    this.layerWindDirection.configure(this.dh, configSynopWindDirectionLayer);
    this.layerWindSpeed.configure(this.dh, configSynopWindSpeedLayer);
    const filter = feature => {
      const windSpdMsec = feature.properties.WNDSPD;
      if (windSpdMsec < 0 || isNaN(windSpdMsec)) {
        return false;
      }
      if (isNaN(feature.properties.WNDDIR_36)) {
        return false;
      }
      feature.properties.WNDSPDKT = Math.round(mps2kt(windSpdMsec));
      feature.properties.WNDSPDKTTEXT = `${feature.properties.WNDSPDKT}kt`;
      if (feature.properties.WNDDIR_36 >= 0) {
        feature.properties.WNDDIR = feature.properties.WNDDIR_36 * 10;
      }
      return true;
    };

    this.layerWindDirection.setAttributes({
      filter: filter
    });
    this.layerWindSpeed.setAttributes({
      filter: filter
    });
    if (this.windDirectionVisible) {
      this.layerWindDirection.show();
    }
    if (this.windSpeedVisible) {
      this.layerWindSpeed.show();
    }
  }

  async drawLayer(visible) {
    this.visible = visible;
    if (!this.ready) {
      await this.initialize();
    }
    this.setVisible(this.visible);
  }

  getLayer() {
    return [this.layerWindDirection,　this.layerWindSpeed, this.layerVis];
  }

  setVisible(visible) {
    if (!this.ready) return;

    if (visible) {
      this.layerVis.show();
      if (this.windDirectionVisible) {
        this.layerWindDirection.show();
      }
      if (this.windSpeedVisible) {
        this.layerWindSpeed.show();
      }
    } else {
      this.layerVis.hide();
      this.layerWindDirection.hide();
      this.layerWindSpeed.hide();
    }

    const enabled = visible && this.autoRefresh;
    if (this.dhAutoUpdate !== enabled) {
      this.dh.setAutoUpdate('timelist', enabled);
      this.dhAutoUpdate = enabled;
    }

    this._updateTimeList();

    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});

  }

  initTimeController() {
    this.synopTimeController.addLayer(this.layerVis);
    this.synopTimeController.addLayer(this.layerWindDirection);
    this.synopTimeController.addLayer(this.layerWindSpeed);
    if (this.visible) {
      this.synopTimeController.setDisplayTime(this.validTime);
    }
  }

  getTimeController() {
    return this.synopTimeController;
  }

  setAutoRefresh(autoFlg) {
    if (autoFlg === this.autoRefresh) return;
    this.autoRefresh = autoFlg;
    if (!this.ready) return;
    this.dh.setAutoUpdate('timelist', this.autoRefresh);
    this.dhAutoUpdate = this.autoRefresh;
  }

  _updateTimeList() {
    let validTimes = this.dh.retained.timelist?.validtime;
    if (!validTimes) return;

    //最新から1時間以内のものにfilter
    validTimes = Array.from(validTimes).sort();
    const latest = new WRAP.Core.DateTime(validTimes.slice(-1)[0]);
    this.timeList = validTimes.filter(vt => {
      const diff = (new WRAP.Core.DateTime(vt)).diff(latest);
      return 0 <= diff && diff <= 3600;
    });

    if (this.autoRefresh) {
      this.synopTimeController.setCurrentTime(this.validTime);
    }

    this.synopTimeController.adjustCurrentTime();
    this.synopTimeController.invalidate();
  }

  _validateTime(data, displayTime, content) {
    const vt = nearValidTime(data.retained.timelist?.validtime, displayTime);

    if (vt) {
      this.validTime = content.validtime = vt;
      this.dh.controller.loadTime(this.dh, this.validTime);
      return true;
    }
    return false;
  }

  changeRoutePointEditing(routePointEditing) {
    this.layerVis.disableInteraction(routePointEditing);
  }

  getTimeInformation() {
    return {
      id: Synop.NAME,
      label: 'SYNOP',
      baseTime: {
        id: Synop.NAME,
        time: this.validTime
      }
    }
  }

  getLegend() {
    return {
      id: Synop.NAME,
      label: 'SYNOP',
      component: LegendSynop,
      contents: this
    };
  }

  getWindDirectionVisible() {
    return this.windDirectionVisible;
  }

  getWindSpeedVisible() {
    return this.windSpeedVisible;
  }

  setWindDirectionVisible(visible) {
    if (visible === this.windDirectionVisible) return;

    this.windDirectionVisible = visible;
    if (this.windDirectionVisible) {
      this.layerWindDirection.show();
    } else {
      this.layerWindDirection.hide();
    }
  }

  setWindSpeedVisible(visible) {
    if (visible === this.windSpeedVisible) return;

    this.windSpeedVisible = visible;
    if (this.windSpeedVisible) {
      this.layerWindSpeed.show();
    } else {
      this.layerWindSpeed.hide();
    }
  }

  _selectValidTime(ref, cb) {
    let targetTime;
    if (!this.autoRefresh) {
      targetTime = this.synopTimeController.getCurrentTime();
    } else {
      targetTime = Date.now();
    }

    const vt = nearValidTime(ref.validtime, targetTime);
    if (this.validTime !== vt) {
      this.validTime = vt;
      cb(vt);
    }

    this._updateTimeList();
    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
  }
}

export default Synop;